import { type RefCallback, createContext, use } from "react";

import type { FilterOption, FilterTypes } from "./filter-types/types";

/**
 * Per-wrapper edit session. The wrapper that owns the filter's dropdown
 * (DropdownTableFilter or InlinePillTableFilter) provides this context so
 * `<FilterInput />` can pull everything it needs without prop drilling.
 *
 * `state` / `actions.onChange` differ between wrappers:
 *   - dropdown writes through to the panel's pending buffer
 *   - inline pill buffers locally and publishes on close
 *
 * `actions.submit` lets a filter input apply + close on Enter.
 */
export interface FilterEditState {
  value: any;
}

export interface FilterEditActions {
  onChange: (value: any) => void;
  submit: () => void;
}

export interface FilterEditMeta {
  type: FilterTypes;
  label: string;
  options?: FilterOption[];
  onBack?: () => void;
  focusRef?: RefCallback<HTMLElement>;
  direction?: "past" | "future";
  openEnded?: boolean;
}

export interface FilterEditContextValue {
  state: FilterEditState;
  actions: FilterEditActions;
  meta: FilterEditMeta;
}

export const FilterEditContext = createContext<FilterEditContextValue | null>(
  null,
);

export function useFilterEdit(): FilterEditContextValue {
  const ctx = use(FilterEditContext);
  if (!ctx) {
    throw new Error(
      "useFilterEdit must be used inside <FilterEditContext.Provider>",
    );
  }
  return ctx;
}
